import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  FormLabel,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { getProfile, updateProfile } from '../api/profileApi';
import { setProfile, setLoading, setError } from '../profileSlice';
import type { ProfileUpdateData } from '../types';
import { toastr } from 'react-redux-toastr';
import AddressInput from '../../../components/common/AddressInput';

const ProfilePage: React.FC = () => {
  const { t } = useTranslation('common');
  const dispatch = useAppDispatch();
  const { profile, loading, error } = useAppSelector((state) => state.profile);

  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState<ProfileUpdateData>({
    name: '',
    lastName: '',
    email: '',
    civility: '',
    birthday: '',
    phoneNumber: '',
    placeOfBirth: '',
    address: {
      street: '',
      postalCode: '',
      city: '',
      country: '',
    },
  });

  const [currentPassword, setCurrentPassword] = useState('');
  const [isEditingEmail, setIsEditingEmail] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    // Fetch profile on mount
    dispatch(setLoading(true));
    getProfile()
      .then((response) => {
        // Le backend retourne { status: "ok", data: { user: {...}, student: {...} } }
        const profileData = response.data.data;

        if (!profileData) {
          dispatch(setError('Aucune donnée de profil reçue'));
          return;
        }

        dispatch(setProfile(profileData));

        const { user, student } = profileData;

        setFormData({
          name: user?.name || '',
          lastName: user?.lastName || '',
          email: user?.email || '',
          civility: student?.civility || '',
          birthday: student?.birthday || '',
          phoneNumber: student?.phoneNumber || '',
          placeOfBirth: student?.placeOfBirth || '',
          address: student?.address || {
            street: '',
            postalCode: '',
            city: '',
            country: '',
          },
        });
      })
      .catch((err) => {
        dispatch(setError(err.response?.data?.message || 'Erreur lors du chargement du profil'));
      });
  }, [dispatch]);

  const handleInputChange = (field: keyof ProfileUpdateData, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleAddressChange = (newAddress: { street: string; postalCode: string; city: string; country: string }) => {
    setFormData((prev) => ({
      ...prev,
      address: newAddress,
    }));
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setIsEditingEmail(false);
    setCurrentPassword('');
    // Reset form to original profile data
    if (profile) {
      const { user, student } = profile;
      setFormData({
        name: user.name,
        lastName: user.lastName,
        email: user.email,
        civility: student?.civility || '',
        birthday: student?.birthday || '',
        phoneNumber: student?.phoneNumber || '',
        placeOfBirth: student?.placeOfBirth || '',
        address: student?.address || {
          street: '',
          postalCode: '',
          city: '',
          country: '',
        },
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);

    try {
      const updateData: ProfileUpdateData = {
        ...formData,
      };

      // Add password if email is being changed
      if (isEditingEmail && formData.email !== profile?.user.email) {
        if (!currentPassword) {
          toastr.error('Erreur', 'Le mot de passe actuel est requis pour changer l\'email');
          setIsSaving(false);
          return;
        }
        updateData.currentPassword = currentPassword;
      }

      const response = await updateProfile(updateData);

      // Le backend retourne { status: "ok", data: { user: {...}, student: {...} } }
      const updatedProfileData = response.data.data;

      if (updatedProfileData) {
        dispatch(setProfile(updatedProfileData));
        toastr.success('Succès', 'Profil mis à jour avec succès');
        setIsEditing(false);
        setIsEditingEmail(false);
        setCurrentPassword('');
      }
    } catch (err: any) {
      toastr.error('Erreur', err.response?.data?.message || 'Erreur lors de la mise à jour');
    } finally {
      setIsSaving(false);
    }
  };

  if (loading && !profile) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error && !profile) {
    return (
      <Box p={3}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">
          {t('profile.title')}
        </Typography>
        {!isEditing && (
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={handleEdit}
            sx={{
              borderColor: '#2f6975',
              color: '#2f6975',
              '&:hover': {
                borderColor: '#26555f',
                backgroundColor: 'rgba(47, 105, 117, 0.04)',
              },
            }}
          >
            {t('profile.edit')}
          </Button>
        )}
      </Box>

      <form onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          {/* Informations personnelles */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {t('profile.personal_info')}
                </Typography>
                <Grid container spacing={2} mt={1}>
                  <Grid item xs={12} sm={12}>
                    <FormControl component="fieldset" disabled={!isEditing}>
                      <RadioGroup
                        row
                        value={formData.civility || ''}
                        onChange={(e) => handleInputChange('civility', e.target.value)}
                        sx={{
                          gap: 4,
                          pl: 2,
                          pt: 1
                        }}
                      >
                        <FormControlLabel
                          value="M"
                          control={<Radio />}
                          label="M"
                          disabled={!isEditing}
                        />
                        <FormControlLabel
                          value="Mme"
                          control={<Radio />}
                          label="Mme"
                          disabled={!isEditing}
                        />
                      </RadioGroup>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label={t('profile.first_name')}
                      value={formData.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      disabled={!isEditing}
                      InputProps={{
                        readOnly: !isEditing,
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label={t('profile.last_name')}
                      value={formData.lastName}
                      onChange={(e) => handleInputChange('lastName', e.target.value)}
                      disabled={!isEditing}
                      InputProps={{
                        readOnly: !isEditing,
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label={t('profile.birth_date')}
                      type="date"
                      value={formData.birthday}
                      onChange={(e) => handleInputChange('birthday', e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      disabled={!isEditing}
                      InputProps={{
                        readOnly: !isEditing,
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label={t('profile.birth_place')}
                      value={formData.placeOfBirth}
                      onChange={(e) => handleInputChange('placeOfBirth', e.target.value)}
                      disabled={!isEditing}
                      InputProps={{
                        readOnly: !isEditing,
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label={t('profile.phone')}
                      value={formData.phoneNumber}
                      onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
                      disabled={!isEditing}
                      InputProps={{
                        readOnly: !isEditing,
                      }}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Email & Password */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {t('profile.email_security')}
                </Typography>
                <Grid container spacing={2} mt={1}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label={t('profile.email')}
                      type="email"
                      value={formData.email}
                      onChange={(e) => {
                        handleInputChange('email', e.target.value);
                        setIsEditingEmail(e.target.value !== profile?.user.email);
                      }}
                      disabled={!isEditing}
                      InputProps={{
                        readOnly: !isEditing,
                      }}
                    />
                  </Grid>
                  {isEditingEmail && isEditing && (
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label={t('profile.current_password')}
                        type="password"
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                        required
                      />
                      <Typography variant="caption" color="text.secondary" mt={1}>
                        {t('profile.email_confirmation_notice')}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Adresse */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {t('profile.address')}
                </Typography>
                <Box mt={2}>
                  <AddressInput
                    value={formData.address || null}
                    onChange={handleAddressChange}
                    disabled={!isEditing}
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Actions */}
          {isEditing && (
            <Grid item xs={12}>
              <Box display="flex" justifyContent="flex-end" gap={2}>
                <Button
                  variant="outlined"
                  startIcon={<CancelIcon />}
                  onClick={handleCancel}
                  disabled={isSaving}
                  sx={{
                    borderColor: '#9e9e9e',
                    color: '#9e9e9e',
                    '&:hover': {
                      borderColor: '#757575',
                      backgroundColor: 'rgba(158, 158, 158, 0.04)',
                    },
                  }}
                >
                  {t('profile.cancel')}
                </Button>
                <Button
                  variant="contained"
                  type="submit"
                  startIcon={<SaveIcon />}
                  disabled={isSaving}
                  sx={{
                    backgroundColor: '#2f6975',
                    '&:hover': { backgroundColor: '#26555f' },
                  }}
                >
                  {isSaving ? <CircularProgress size={24} /> : t('profile.save')}
                </Button>
              </Box>
            </Grid>
          )}
        </Grid>
      </form>
    </Box>
  );
};

export default ProfilePage;
